import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetGenericEvent} from 'features/core/shared/widget/event/widget-generic-event';
import {
    MvsWidgetFormGenericEventConstants
} from 'features/core/shared/widget/component/runtime/implementations/mvs-widget-form/mvs-widget-form-generic-event.constants';
import {MvsFormGroup} from 'features/core/shared/logic/form/mvs-form-group';
import {MvsFormDto} from 'features/core/shared/dto/form/dto/mvs-form.dto';
import {MvsFormFieldDto} from 'features/core/shared/dto/form/dto/mvs-form-field.dto';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from 'features/core/shared/logic/object-change-information';
import {CpProvisionContractAgentService} from '../../../../../service/api/cp-provision-contract-agent.service';
import {ProvisionContractAgentDto} from '../../../../../model/dto/entity/provision-contract-agent.dto';
import {CpFeatureRight} from '../../../../../service/domain/cp-feature-right';

/**
 * Action dialog "Satz manuell anlegen" (04-frontend.md §7) —
 * `POST /mvsa/cp/provisionContractAgents`; um right `cp_agentChange` (AX-08 adds no new right).
 *
 * ── WHY A MANUALLY CREATED SATZ EXISTS AT ALL
 *
 * The engine resolves the Berater of an event from the contract's `cm.ContractAgent` rows via the
 * Provisionsart's `agentRole`. When no role matched — a Nachtrag, a role that was never maintained
 * — the event produces no rate row and therefore no booking, and no later run repairs it on its
 * own. This dialog is the repair: it creates the missing row by hand. `source = MANUAL` is FORCED
 * by {@link CpProvisionContractAgentService.createManual}, not offered as a field, and that is what
 * makes the repair durable — a MANUAL row is never replaced or deleted by a recalculation
 * (E6/A6-11), while an AUTOMATIC one would be gone with the next run.
 *
 * ── WHAT THE OPERATOR PICKS, AND WHAT THE BACKEND DERIVES
 *
 * §7 names three inputs and this dialog asks for exactly those three: the Ereigniszeile
 * (`cp.ProvisionContract`), the Provisionsart (`cp.ProvisionTypeComponent`) and the Berater — via
 * their Abrechnungskonto `cp.ProvisionAgent`, which is the entity the rate row references (§1
 * terminology). Everything else the row needs is DERIVED by the write path from those three: the
 * denormalised `contract`, `provisionType` and `provisionTypeContractEvent` (A6-04), the
 * `agentRole` of the chosen Provisionsart, and the liability frame that follows from the event's
 * own period (A4-08). The frontend deliberately does not invent any of them — inventing a liability
 * start here would create a row the engine then disagrees with.
 *
 * ── FORM MECHANISM (design gate finding B-9 — mandatory documentation)
 *
 * TRANSIENT FORM WIDGET built from an `MvsFormDto` constructed in the frontend, identical to the
 * sibling dialogs of this OBC (repo evidence `wf-process-start.component.ts`). The three fields are
 * declared as ENTITY REFERENCES (`dataType` + `relationship = 'ManyToOne'`) so the framework renders
 * its own FK picker with search — the same construction the manual booking dialog uses for its
 * optional `provisionContractDtoId`. Value lists were rejected for them: `cp.ProvisionContract` has
 * one row per contract, provision type and event, so a transferred list would be truncated and the
 * wanted line would silently not be among the options. This component contains NO form HTML.
 */
@Component({
    selector: 'cp-provision-agent-manual-rate-dialog',
    templateUrl: './cp-provision-agent-manual-rate-dialog.component.html',
    styleUrls: ['./cp-provision-agent-manual-rate-dialog.component.scss'],
    standalone: false
})
export class CpProvisionAgentManualRateDialogComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    /** The Abrechnungskonto the pane belongs to; shown as context, not written from here. */
    @Input() provisionAgentId: number;

    /** Display name of the Abrechnungskonto; purely informational. */
    @Input() provisionAgentName: string;

    /** um right `cp_agentChange` (U-08) — optimistic default, demoted on 403; see CpFeatureRight. */
    @Input() canAgentChange: boolean = true;

    /** Visibility of the dialog; intended as a two-way binding (`[(visible)]`). */
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    /** Fires after a successfully created Provisionssatz. */
    @Output() onRateCreated = new EventEmitter<ProvisionContractAgentDto>();

    /** Fires when the server refused the right with a 403 — the demotion signal. */
    @Output() onForbidden = new EventEmitter<void>();

    /** The transient capture widget (see class Javadoc). */
    formWidget: WidgetData;

    /** Last VALID form value; null as long as the form is incomplete/invalid. */
    formValue: any = null;

    /** Backend error message on a technical or validation failure. */
    failureMessage: string = null;

    constructor(
        protected mvsObjectService: MvsObjectService,
        protected messageService: MvsMessageService,
        protected provisionContractAgentService: CpProvisionContractAgentService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.initialized = true;
        if (this.visible) {
            this.refreshComponent();
        }
    }

    /** Rebuilt fresh on every open — a value left over from a previous attempt must not be sent. */
    refreshComponent(): void {
        this.formValue = null;
        this.failureMessage = null;
        this.formWidget = null;
        this.buildFormWidget();
    }

    /**
     * Builds the `MvsFormDto` and the transient form widget from it. The field ids are the
     * attribute names of {@link ProvisionContractAgentDto} 1:1, so {@link handleCreate} assembles
     * the DTO without any renaming.
     */
    private buildFormWidget(): void {

        const form = new MvsFormDto();
        const formFields: MvsFormFieldDto[] = [];

        // Ereigniszeile — MANDATORY. The event time slice the new rate row hangs on.
        const provisionContractField = new MvsFormFieldDto();
        provisionContractField.id = 'provisionContractDtoId';
        provisionContractField.dataType = 'cp.ProvisionContract';
        provisionContractField.relationship = 'ManyToOne';
        provisionContractField.access = 'update';
        provisionContractField.uiMandatory = true;
        provisionContractField.uiLabel = 'Ereigniszeile';
        provisionContractField.uiDescription =
            'Das Ereignis (Vertrag, Provisionstyp, Zeitpunkt), für das der Satz entstehen soll.';
        provisionContractField.order = 10;
        formFields['provisionContractDtoId'] = provisionContractField;

        // Provisionsart — MANDATORY. Determines the role, the matrix and the reserve rate.
        const provisionTypeComponentField = new MvsFormFieldDto();
        provisionTypeComponentField.id = 'provisionTypeComponentDtoId';
        provisionTypeComponentField.dataType = 'cp.ProvisionTypeComponent';
        provisionTypeComponentField.relationship = 'ManyToOne';
        provisionTypeComponentField.access = 'update';
        provisionTypeComponentField.uiMandatory = true;
        provisionTypeComponentField.uiLabel = 'Provisionsart';
        provisionTypeComponentField.uiDescription =
            'Bestimmt Rolle, Matrix und Rücklagensatz des Provisionssatzes.';
        provisionTypeComponentField.order = 20;
        formFields['provisionTypeComponentDtoId'] = provisionTypeComponentField;

        // Berater — MANDATORY, referenced through their Abrechnungskonto (§1 terminology).
        const provisionAgentField = new MvsFormFieldDto();
        provisionAgentField.id = 'provisionAgentDtoId';
        provisionAgentField.dataType = 'cp.ProvisionAgent';
        provisionAgentField.relationship = 'ManyToOne';
        provisionAgentField.access = 'update';
        provisionAgentField.uiMandatory = true;
        provisionAgentField.uiLabel = 'Berater (Abrechnungskonto)';
        provisionAgentField.uiDescription =
            'Das Abrechnungskonto, auf das dieser Provisionssatz gebucht wird.';
        provisionAgentField.order = 30;
        formFields['provisionAgentDtoId'] = provisionAgentField;

        form.formFields = formFields;

        /**
         * AI: Transient form widget for capturing a manually created Provisionssatz.
         *     Source: MvsFormDto built in the frontend with three entity-reference fields
         *     (Ereigniszeile, Provisionsart, Abrechnungskonto) — the write goes to
         *     POST /cp/provisionContractAgents with source forced to MANUAL.
         *     Caseworkers use it to repair an event whose Berater the engine could not resolve.
         */
        this.formWidget = WidgetFactory.createWidget(
            'cp.agent.manualContractAgent.form',
            'Satz manuell anlegen',
            'form',
            'transient',
            'transient',
            'cp.ProvisionContractAgent',
            WidgetDataParam.create('size', 'S'),
            WidgetDataParam.create('form', form)
        );
    }

    /** Receives the form values; only a fully VALID form unlocks the submit button. */
    handleGenericEvent(event: WidgetGenericEvent): void {

        if (event.eventName !== MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE) {
            return;
        }

        const formGroup: MvsFormGroup =
            event.object[MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE_ATTR_FORM_GROUP];

        this.formValue = formGroup.status === 'VALID' ? formGroup.value : null;
        this.failureMessage = null;
    }

    /** Submit is open only with the right, a valid form and no request in flight. */
    get submittable(): boolean {
        return this.canAgentChange && !!this.formValue && !this.busy;
    }

    /** Why the submit button is locked, in German — one reason per cause. */
    get submitTooltip(): string {

        if (!this.canAgentChange) {
            return CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
        }

        if (!this.formValue) {
            return 'Bitte Ereigniszeile, Provisionsart und Berater auswählen.';
        }

        return 'Provisionssatz anlegen.';
    }

    /**
     * Creates the Satz. `source = MANUAL` is set by the service, not here — one place, so the
     * guarantee cannot be forgotten at a second call site.
     */
    handleCreate(): void {

        if (!this.submittable) {
            return;
        }

        const dto = new ProvisionContractAgentDto();
        dto.provisionContractDtoId = Number(this.formValue['provisionContractDtoId']);
        dto.provisionTypeComponentDtoId = Number(this.formValue['provisionTypeComponentDtoId']);
        dto.provisionAgentDtoId = Number(this.formValue['provisionAgentDtoId']);

        this.busy = true;
        this.failureMessage = null;

        this.provisionContractAgentService.createManual(dto).subscribe({
            next: (result: ProvisionContractAgentDto) => {
                this.busy = false;

                this.messageService.showSuccessMessage(
                    'Provisionssatz angelegt',
                    'Der Satz ist manuell und wird von künftigen Läufen nicht überschrieben.');

                this.informObjectChanged();
                this.onRateCreated.emit(result);
                this.handleClose();
            },
            error: (error) => {
                this.busy = false;
                this.demoteOnForbidden(error);
                this.failureMessage = CpFeatureRight.isForbidden(error)
                    ? CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE)
                    : 'Der Provisionssatz konnte nicht angelegt werden. Bitte Eingaben prüfen — '
                    + 'je Ereignis, Provisionsart und Abrechnungskonto ist nur ein Satz zulässig.';
            }
        });
    }

    /**
     * ONLY a 403 demotes: a 500 or a dropped connection must leave the flag alone, otherwise a
     * transient backend fault would silently take the action away from a user who holds the right.
     */
    private demoteOnForbidden(error: any): void {
        if (CpFeatureRight.isForbidden(error)) {
            this.canAgentChange = false;
            this.onForbidden.emit();
        }
    }

    /** Tell the module that a Provisionssatz appeared — the hosting list reloads on it. */
    private informObjectChanged(): void {
        this.mvsObjectService.objectChangeEvent.next(
            ObjectChangeInformation.create(
                'cp.ProvisionContractAgent',
                ObjectChangeInformationActionEnum.created,
                null,
                null));
    }

    handleShow(): void {
        this.refreshComponent();
    }

    handleClose(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    handleVisibleChange(value: boolean): void {
        this.visible = value;
        this.visibleChange.emit(value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['visible'] && this.visible) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
    }

}
